import { useApi } from '@composables/useApi';
import { defineStore } from 'pinia';
import {
	FailedCreateReservePackInvoiceError,
	FailedGetReservePacksError,
	FailedGetReserveStateError,
	PaymentInvoice,
	ReservePack,
	ReserveState
} from '@store/api/interfaces';
import { useAuthApi } from '@store/api/authApi';
import type { InvoiceOptions } from '@store/api/paymentApi';

/**
 * «Резервное подключение»: состояние резерва и покупка пакетов ГБ.
 *
 * Отдельный стор, а не часть paymentApi: состояние живёт в БД бота и читается
 * best-effort, а каталог пакетов не пересекается с тарифами подписки.
 */
export const useReserveApi = defineStore('reserveApi', () => {
	const authApi = useAuthApi();

	// Каталог пакетов публичный, как и тарифы: без токена он тоже отвечает.
	const getOptionalAuthHeaders = (): HeadersInit => {
		const token = authApi.methods.getToken();
		if (!token) return {};
		return { 'Authorization': `Bearer ${token}` };
	};

	const http = {
		// Требует авторизации. Отвечает 200 всегда: пустые блоки внутри — это
		// «нет данных», а не ошибка, поэтому разбирать их — дело вызывающего.
		getState: async (): Promise<ReserveState> => {
			try {
				return await useApi<ReserveState>('/reserve', {
					method: "GET",
					headers: authApi.methods.getAuthHeaders()
				});
			} catch (error: any) {
				console.error('failed get reserve state', error);
				throw new FailedGetReserveStateError(error.message);
			}
		},

		// Пустой список = продажи резерва выключены, раздел просто не показываем.
		getPacks: async (): Promise<ReservePack[]> => {
			try {
				return await useApi<ReservePack[]>('/payment/reserve/packs', {
					method: "GET",
					headers: getOptionalAuthHeaders()
				});
			} catch (error: any) {
				console.error('failed get reserve packs', error);
				throw new FailedGetReservePacksError(error.message);
			}
		},

		/**
		 * Счёт на пакет. Анонимной покупки нет, промокоды к пакетам не применяются —
		 * поэтому ни email, ни promo_code сюда не передаются, только GA-контекст:
		 * покупку подтверждает бот, и ему нужен client_id покупателя.
		 *
		 * Ошибки, которые обязан обработать интерфейс: 409 `subscription_not_active`
		 * (нет оплаченной подписки — сначала оплатить её; старый бэкенд отдаёт
		 * `reserve_not_active`) и 403 `reserve_sales_disabled`.
		 */
		packInvoice: async (packId: number, providerCode: string, data: object, options?: InvoiceOptions): Promise<PaymentInvoice> => {
			try {
				const body: Record<string, any> = { pack: packId, provider: providerCode, data };
				if (options?.gaClientId) {
					body.ga_client_id = options.gaClientId;
				}
				if (options?.gaSessionId) {
					body.ga_session_id = options.gaSessionId;
				}
				if (options?.gaStatus) {
					body.ga_status = options.gaStatus;
				}
				if (options?.gaSessionAgeSec !== undefined) {
					body.session_age_sec = options.gaSessionAgeSec;
				}
				if (options?.gtmLoaded !== undefined) {
					body.gtm_loaded = options.gtmLoaded;
				}
				if (options?.capturedAt) {
					body.captured_at = options.capturedAt;
				}
				if (options?.source) {
					body.source = options.source;
				}
				if (options?.pageLocation) {
					body.page_location = options.pageLocation;
				}
				return await useApi<PaymentInvoice>('/payment/reserve/invoice', {
					method: "POST",
					headers: authApi.methods.getAuthHeaders(),
					body: JSON.stringify(body)
				});
			} catch (error: any) {
				console.error('failed create reserve pack invoice', error);
				throw new FailedCreateReservePackInvoiceError(error.message, error.code);
			}
		}
	};

	const methods = {};

	return {
		http,
		methods
	};
});
